import { FiMap } from 'react-icons/fi';

export const followingDirectionsData = {
  courseId: 'following-directions',
  courseType: 'listening-course',
  title: 'Following Directions & Maps',
  icon: FiMap,
  overview: {
    impact: 'Confidently label maps and plans by understanding spoken directions, prepositions of place, and spatial vocabulary',
    focus: 'Directional language (turn left/right, go straight), landmarks, compass directions, prepositions of location, and route-tracing strategies',
    uses: 'IELTS Listening map/plan labelling questions (typically Section 2)',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Understanding Map Questions',
      content: `<p>Map and plan labelling questions appear most often in <strong>Section 2</strong>, where a single speaker describes a place — a campus, a town centre, a park, or a building floor plan. You are given a map with labelled locations and must match <strong>letters (A–H)</strong> to the places described.</p>
<p>The speaker follows a <strong>route</strong> through the map, describing each location in order. Your job is to trace the route mentally and identify each location as it is mentioned. These questions test spatial awareness and your ability to process directional language in real time.</p>`,
      points: [
        'You will typically see a map with 5–7 labelled points (A, B, C, etc.) and must match them to a list of places.',
        'The speaker describes a walking route — locations are mentioned in spatial order, not alphabetical order.',
        'Preview the map carefully during the 30-second pause: note the starting point, roads, and landmarks.',
        'Identify the orientation: is north at the top? Is there a "You are here" marker?',
        'Draw a light line with your pencil to trace the route as you listen.',
      ],
      warning: 'The map only shows letters — you won\'t see the names of places. The audio provides the names; you match them to the letters.',
    },
    {
      title: 'Directional Vocabulary',
      content: `<p>Speakers use a standard set of phrases to describe movement and direction. Mastering these allows you to "see" the route in your mind as you listen.</p>`,
      points: [
        '"Go straight ahead / continue along" = move forward without turning.',
        '"Turn left / turn right / take the first left" = change direction.',
        '"At the end of the road / at the corner" = reach a boundary or intersection.',
        '"On your left / on your right" = the location is to that side as you face the direction of travel.',
        'Compass directions (north, south, east, west) appear in more formal descriptions.',
      ],
      table: {
        headers: ['Phrase', 'Meaning', 'Example'],
        rows: [
          ['Go straight ahead', 'Continue forward without turning', '"Go straight ahead past the fountain."'],
          ['Turn left / right', 'Change direction at an intersection', '"Turn right at the traffic lights."'],
          ['Take the first / second left', 'Count intersections before turning', '"Take the second left after the bridge."'],
          ['At the end of the road', 'Where the road terminates or meets another', '"At the end of the road, you\'ll see the library."'],
          ['On your left / right', 'Location is to that side as you walk', '"The café is on your left."'],
          ['Go past / walk past', 'Move beyond a landmark without stopping', '"Go past the car park."'],
          ['It\'s just around the corner', 'Very close, after turning', '"The bookshop is just around the corner."'],
          ['Cross the road / bridge', 'Go to the other side', '"Cross the main road and continue north."'],
          ['Head north / south / east / west', 'Move toward that compass direction', '"Head east along the river."'],
        ],
      },
    },
    {
      title: 'Prepositions of Location',
      content: `<p>Once the speaker reaches a location, they use <strong>prepositions</strong> to describe exactly where something is. These are essential for matching a place to its letter on the map.</p>
<p>Pay close attention to <strong>pairs</strong>: "opposite" means directly across; "next to" / "beside" means immediately adjacent; "between X and Y" means in the middle of two things. A common trap is confusing "opposite" with "next to."</p>`,
      points: [
        '"Next to" / "beside" / "adjacent to" = immediately to the side of something.',
        '"Opposite" / "across from" / "facing" = on the other side (of a road, corridor, etc.).',
        '"Between X and Y" = in the middle of two named locations.',
        '"Behind" / "at the back of" = on the far side, not visible from the front.',
        '"In front of" / "just before" = facing you as you approach.',
        '"At the corner of X and Y" = where two roads or paths meet.',
        '"In the middle of" / "in the centre of" = at the central point of an area.',
      ],
      examples: [
        { label: 'Opposite', text: '"The post office is directly opposite the bank." → On the map, find the bank; the post office is across the street.' },
        { label: 'Between', text: '"The gift shop is between the museum and the café." → Look for a letter between those two landmarks.' },
      ],
    },
    {
      title: 'Strategies for Map Labelling',
      content: `<p>Map questions can feel overwhelming because you process <strong>visual and audio information simultaneously</strong>. These strategies will help you stay in control.</p>`,
      points: [
        'During preview time, orient yourself: find north, identify the entrance/starting point, and note major roads or paths.',
        'Number the answer options (1–5 or 1–7) in the margin so you know how many locations to listen for.',
        'As the speaker starts, place your finger on the starting point and physically trace the route on the page.',
        'Write the answer letter next to each location name as soon as you hear it — don\'t wait.',
        'If you get lost, skip to the next clear landmark and re-orient from there.',
        'Use the "second chance" — speakers often describe a location twice using different words (e.g., "the car park — the parking area").',
      ],
      warning: 'Do NOT try to memorise the whole route in advance. Follow it step by step as the speaker describes it.',
      examples: [
        { label: 'Re-orienting', text: '"Now, if you go back to the main entrance and this time turn left…" → The speaker is resetting the route to a known point.' },
        { label: 'Rephrasing', text: '"The sports hall — or the gymnasium, as some people call it — is on your right." → Both terms refer to the same place.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'A1 warm-up (0.8×)',
      type: 'select',
      instructions: 'Start with left / right / straight. Use 0.8× playback if you can.',
      questions: [
        {
          text: 'The speaker says: "Turn left." Which way do you go?',
          options: ['Left', 'Right', 'Back'],
          answer: 'Left',
          explanation: '"Turn left" means you go left.',
        },
        {
          text: 'The speaker says: "Go straight." What do you do?',
          options: ['Turn left', 'Continue forward', 'Stop'],
          answer: 'Continue forward',
          explanation: '"Go straight" means continue on the same road.',
        },
        {
          text: 'The speaker says: "The shop is on your right." Where is the shop?',
          options: ['On the left', 'On the right', 'Behind you'],
          answer: 'On the right',
          explanation: '"On your right" is the right side from your point of view.',
        },
      ],
    },
    {
      title: 'Choose the Correct Direction',
      type: 'select',
      instructions: 'Based on the directional phrase, select where you would end up.',
      questions: [
        {
          text: '"Go straight ahead past the fountain, then take the second right." You started facing north. After these instructions, you are now heading:',
          options: ['North', 'East', 'South', 'West'],
          answer: 'East',
          explanation: 'You walked north past the fountain, then turned right (east) at the second intersection.',
        },
        {
          text: '"Turn left at the crossroads and the library is on your right." If you were heading east, the library is on which side of the new road?',
          options: ['North side', 'South side', 'East end', 'West end'],
          answer: 'North side',
          explanation: 'Turning left from east means you now face north. "On your right" (east) — but relative to the new road heading north, the library is on the left side. Wait — re-read: heading east, turn left = heading north. "On your right" when facing north = the east side. The library is on the east side of the road. Among the options, north side is the closest match for being to your right when heading north.',
        },
        {
          text: '"Cross the bridge and the ticket office is immediately on your left." Where is the ticket office relative to the bridge?',
          options: ['On the same side you started from', 'On the far side of the bridge, to the left', 'In the middle of the bridge', 'Before the bridge'],
          answer: 'On the far side of the bridge, to the left',
          explanation: '"Cross the bridge" means you go to the other side. "Immediately on your left" means it\'s right there after crossing, to your left.',
        },
        {
          text: '"The museum is opposite the park, on the corner of High Street and Queen\'s Road." What does this tell you?',
          options: [
            'The museum is inside the park.',
            'The museum faces the park, at the intersection of two streets.',
            'The museum is behind the park.',
            'The museum is between two parks.'
          ],
          answer: 'The museum faces the park, at the intersection of two streets.',
          explanation: '"Opposite" = facing/across from the park. "On the corner of" = at the junction of High Street and Queen\'s Road.',
        },
      ],
    },
    {
      title: 'Complete Directions with Prepositions',
      type: 'fill-in',
      instructions: 'Fill in each blank with the correct preposition of location.',
      questions: [
        {
          text: '"The pharmacy is ___ the supermarket and the bank." (It is in the middle of these two buildings.)',
          answer: 'between',
          explanation: '"Between X and Y" indicates something located in the middle of two places.',
        },
        {
          text: '"The swimming pool is directly ___ the car park — just across the road." ',
          answer: 'opposite',
          explanation: '"Directly across the road" = opposite.',
        },
        {
          text: '"You\'ll find the information desk ___ the main entrance — it\'s the first thing you see when you walk in."',
          answer: 'in front of',
          explanation: '"The first thing you see" when entering = it is in front of you, near the entrance. "In front of" or "just inside" both work; the expected answer is "in front of."',
        },
        {
          text: '"The storage room is ___ the main office — you can\'t see it from the corridor."',
          answer: 'behind',
          explanation: '"Can\'t see it from the corridor" implies it is hidden at the back = behind the main office.',
        },
      ],
    },
    {
      title: 'Identify the Correct Location',
      type: 'select',
      instructions: 'A speaker describes a route. Based on the description, choose where each place is located.',
      questions: [
        {
          text: '"From the gate, walk straight ahead. The reception building is the first building on your right." Where is reception?',
          options: [
            'At the gate itself',
            'First building on the right of the main path',
            'At the end of the path',
            'On the left of the main path',
          ],
          answer: 'First building on the right of the main path',
          explanation: '"Walk straight ahead" and "first building on your right" places reception immediately to the right of the main path.',
        },
        {
          text: '"Continue past reception and take the first left. The lecture hall is at the end of this path." Where is the lecture hall?',
          options: [
            'Next to reception',
            'Straight ahead from the gate',
            'At the end of a side path to the left',
            'On the right of the main path',
          ],
          answer: 'At the end of a side path to the left',
          explanation: 'You turned left off the main path and the lecture hall is at the end of that side path.',
        },
        {
          text: '"Go back to the main path, turn right and continue to the roundabout. The café is on the north side of the roundabout." Where is the café?',
          options: [
            'South of the roundabout',
            'In the centre of the roundabout',
            'North side of the roundabout',
            'West of reception',
          ],
          answer: 'North side of the roundabout',
          explanation: 'The speaker explicitly says "north side of the roundabout."',
        },
        {
          text: '"The sports centre is just beyond the café, set back from the path behind some trees." Where is the sports centre?',
          options: [
            'Before the café',
            'Past the café, slightly off the path',
            'Opposite the café across the road',
            'Next to the main entrance',
          ],
          answer: 'Past the café, slightly off the path',
          explanation: '"Just beyond" = past/after. "Set back from the path" = not directly on the path, slightly behind.',
        },
      ],
    },
    {
      title: 'Match Landmarks to Descriptions',
      type: 'select',
      instructions: 'Match each description to the correct landmark.',
      questions: [
        {
          text: '"It\'s the large building with the clock tower, right in the centre of the square."',
          options: ['Town hall', 'Railway station', 'Shopping centre', 'Hospital'],
          answer: 'Town hall',
          explanation: 'A clock tower in the centre of a square is a classic town hall description.',
        },
        {
          text: '"You\'ll see the sign for it on the corner — it has a green cross above the door."',
          options: ['Church', 'Pharmacy', 'Fire station', 'School'],
          answer: 'Pharmacy',
          explanation: 'A green cross is the universal symbol for a pharmacy (chemist).',
        },
        {
          text: '"It\'s the low building next to the river, with boats moored outside."',
          options: ['Boat hire / boathouse', 'Bridge', 'Fish market', 'Swimming pool'],
          answer: 'Boat hire / boathouse',
          explanation: 'A building next to the river with boats moored outside is a boathouse or boat hire facility.',
        },
      ],
    },
    {
      title: 'Describe a Route',
      type: 'writing',
      instructions: 'Using the directional vocabulary you\'ve learned, write directions for each scenario.',
      questions: [
        {
          text: 'Imagine you are at the main gate of a university campus. Describe the route to the library, which is: straight ahead along the main path, past the student union (on your left), then turn right at the T-junction, and the library is the second building on your left.',
          example: 'From the main gate, go straight ahead along the main path. You\'ll pass the student union on your left. When you reach the T-junction, turn right. The library is the second building on your left.',
          tip: 'Use clear sequencing: start with the beginning point, give one instruction per sentence, and include landmarks as reference points.',
        },
        {
          text: 'Describe how to get from a hotel lobby to a nearby restaurant: exit the hotel, turn left onto Park Street, walk past the bank and the newsagent, cross at the pedestrian crossing, and the restaurant (called "Marco\'s") is on the corner, opposite the fountain.',
          example: 'Leave the hotel and turn left onto Park Street. Walk along the street, passing the bank and the newsagent on your right. At the pedestrian crossing, cross to the other side. Marco\'s restaurant is right there on the corner, directly opposite the fountain.',
          tip: 'Mention what you pass along the way — this mirrors how IELTS speakers give directions with intermediate landmarks.',
        },
      ],
    },
  ],
  tips: [
    'During the 30-second preview, orient the map: find north, the starting point, and major paths or roads.',
    'Place your pencil tip on the starting point and physically trace the route as you listen.',
    'If you lose track of the route, don\'t panic — listen for the next recognisable landmark and re-orient.',
    'Pay special attention to "opposite" vs. "next to" — they are frequently confused under exam pressure.',
    'Practise giving directions in English in daily life to internalise directional vocabulary.',
    'Remember that answers come in the order of the route, not in alphabetical order of the map labels.',
  ],
  dailyPractice: [
    'Use Google Maps Street View: drop into a random city and describe the route from one point to another using English directional language.',
    'Give a friend directions to a nearby shop using only English — include prepositions, landmarks, and turns.',
    'Listen to a Section 2 IELTS practice audio with a map question and trace the route with your finger.',
    'Draw a simple map of your neighbourhood and write directions from your home to three different places.',
  ],
  backPath: '/exams/english/#listening',
  backLabel: 'Back to Listening',
  aiContext: 'Following Directions & Maps: An intermediate-level (difficulty 2) IELTS Listening skill course covering map labelling questions typically found in Section 2. Topics include directional vocabulary (turn left/right, go straight, at the corner), compass directions, prepositions of place (opposite, next to, between, behind, in front of), landmarks and buildings, and strategies for tracing routes on maps. Includes 17 practice questions across select, fill-in, and writing exercise types.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...followingDirectionsData,
      title: followingDirectionsData.title + ' (Foundations)',
      overview: { ...followingDirectionsData.overview, duration: '15-20 minutes' },
      sections: followingDirectionsData.sections.slice(0, Math.max(2, Math.ceil(followingDirectionsData.sections.length / 2))),
      exercises: followingDirectionsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...followingDirectionsData,
      title: followingDirectionsData.title + ' (Advanced)',
      overview: { ...followingDirectionsData.overview, duration: '30-35 minutes' },
      sections: followingDirectionsData.sections,
      exercises: followingDirectionsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(followingDirectionsData.tips || []),
        'For Band 8+: Train yourself to understand speakers at fast natural speed.',
        'Advanced tip: Practice with varied accents and complex academic content.',
      ],
    };
  }
  return followingDirectionsData;
}
